import React, {useEffect, useMemo, useState} from "react";
import {inject, observer} from "mobx-react";
import {fetch} from "../utils/api-wrapper";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencilAlt, faSpinner, faWindowClose} from "@fortawesome/free-solid-svg-icons";
import FilterInputField from "../components/filter-input-field";
import PaginationControls from "../components/pagination-controls";
import Checkbox from "../components/checkbox";
import Switch from "react-switch";
import toast from "react-hot-toast";
import Modal from "../components/modal";
import ExpenseRuleEditModal from "../components/expense-rule-edit-modal/expense-rule-edit-modal";
import HoverInfoCard from "../components/hover-card";
import {
    buildImputationLookup,
    categoryConditionTargets,
    categoryTargetSummaryLabel,
} from "../utils/expense-rule-imputation-targets";
import {
    normalizeCategorySpendLimitParameters,
} from "../utils/expense-rule-amount-basis";
import {
    collectExpenseRuleValidationErrorKeys,
    formatExpenseRuleValidationError,
} from "../utils/expense-rule-validation";
import { IMPUTATION_CATEGORY_SORTING, IMPUTATION_FETCH_PAGE_SIZE } from "../utils/expenses-imputations-config-helpers";

const DEFAULT_SCOPE = "expense";
const DEFAULT_PHASE = "pre_submit";
const DEFAULT_CONDITION_LOGIC = "all";
const DEFAULT_EFFECT = "exception_required";

const createEmptyCondition = (scope = DEFAULT_SCOPE) => ({
    type: scope === "expense_account" ? "monthly_submitted_report_count" : "category_spend_limit",
    parameters: {},
});

const ExpensesRules = ({data}) => {
    const [loading, setLoading] = useState(false);
    const [rules, setRules] = useState([]);
    const [showRuleModal, setShowRuleModal] = useState(false);
    const [updatedRule, setUpdatedRule] = useState({});
    const [ruleValidationError, setRuleValidationError] = useState(null);
    const [rulesDeleteSelection, setRulesDeleteSelection] = useState([]);
    const [showDeleteRuleModal, setShowDeleteRuleModal] = useState(false);

    const [clientImputations, setClientImputations] = useState([]);


    const [pageSize, setPageSize] = useState(10);
    const [filterValue, setFilterValue] = useState("");
    const [sorting, setSorting] = useState([{"id": "names", "desc": false}]);
    const [page, setPage] = useState(1);
    const [totalRowCount, setTotalRowCount] = useState(0);

    useEffect(() => {
        refreshRules()
        fetchClientImputations()
    }, [])

    useEffect(() => {
        setPage(1);
        refreshRules();
    }, [pageSize, sorting, filterValue]);

    useEffect(() => {
        refreshRules();
    }, [page]);

    const refreshRules = () => {
        let start = (page - 1) * pageSize;
        const filters = []
        if (filterValue && filterValue.trim() !== "") {
            filters.push({id: "search", value: filterValue.trim()});
        }
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(start)}&page_size=${pageSize}`;
        fetch(`/expenses_rules/${searchData}`, "GET", {},
            response => {
                let fetchedRules = response.data.expenses_rules;
                setRules(fetchedRules);
                setTotalRowCount(response.data.meta.totalRowCount);
                setLoading(false)
            });
    }

    const handleRuleSubmit = (e) => {
        e.preventDefault();
        const validationError = validateRule(updatedRule);
        if (validationError) {
            setRuleValidationError(validationError);
            return;
        }
        setShowRuleModal(false)
        setRuleValidationError(null);
        if (updatedRule._id) {
            updateRule()
        } else {
            createRule()
        }
    };

    const createRule = () => {
        let formData = new FormData();
        formData.append("expenses_rules", JSON.stringify(prepareRuleForSubmit(updatedRule)));

        fetch("/expenses_rules/", "POST", formData,
            () => {
                toast.success(data.t.read("rule_created_successfully"));
                refreshRules();
            },
            () => {
                toast.error(data.t.read("rule_creation_failure"));
                setLoading(false);
            }
        );
    };

    const updateRule = (rule = { ...updatedRule }) => {
        const updatedRuleWithoutId = prepareRuleForSubmit(rule)
        delete updatedRuleWithoutId._id;
        let formData = new FormData();
        formData.append("expenses_rules", JSON.stringify(updatedRuleWithoutId));
        const ruleId = rule._id?.$oid;
        fetch(`/expenses_rules/${ruleId}`, "PUT", formData,
            () => {
                refreshRules()
                toast.success(data.t.read("update_successful"))
            },
            () => {
                toast.error(data.t.read("failed_to_update"))
                setLoading(false);
            }
        )
    };

    const fetchClientImputations = () => {
        // Displacement imputations must not appear in rule conditions — scope to expense catalog only.
        // Paginated loop so large catalogs are not silently truncated at the API default page size.
        const filters = [{ id: "imputation_catalog", value: "expense" }];
        const all = [];
        const fetchPage = (start) => {
            const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(IMPUTATION_CATEGORY_SORTING))}&start=${start}&page_size=${IMPUTATION_FETCH_PAGE_SIZE}`;
            fetch(`/imputations/${searchData}`, "GET", {}, response => {
                const rows = response?.data?.imputations ?? [];
                const totalRowCount = response?.data?.meta?.totalRowCount ?? 0;
                all.push(...rows);
                if (rows.length > 0 && all.length < totalRowCount) {
                    fetchPage(start + rows.length);
                } else {
                    setClientImputations(all);
                }
            });
        };
        fetchPage(0);
    }

    const openNewRuleModal = () => {
        setUpdatedRule(
            {
                id_client: data.id_client,
                name: "",
                description: "",
                active: false,
                scope: DEFAULT_SCOPE,
                phase: DEFAULT_PHASE,
                effect: DEFAULT_EFFECT,
                condition_logic: DEFAULT_CONDITION_LOGIC,
                conditions: [createEmptyCondition(DEFAULT_SCOPE)],
                message: "",
            });
        setRuleValidationError(null);
        setShowRuleModal(true);
    };

    const openUpdateRuleModal = (rule) => {
        setUpdatedRule(normalizeRuleForEdit(rule));
        setRuleValidationError(null);
        setShowRuleModal(true)
    }

    const cancelRuleModal = () => {
        setUpdatedRule({});
        setRuleValidationError(null);
        setShowRuleModal(false);
    }

    const deleteRules = () => {
        fetch("/expenses_rules/", "DELETE", {"expenses_rules_ids": rulesDeleteSelection},
            () => {
                refreshRules();
                setRulesDeleteSelection([])
                setShowDeleteRuleModal(false)
                toast.success(data.t.read("delete"));
            },
            (error) => {
                toast.error(data.t.read("failed_to_delete"))
                setLoading(false);
                setShowDeleteRuleModal(false)
            }
        );
    }

    const cancelDeleteRuleModal = () => {
        setShowDeleteRuleModal(false);
        setRulesDeleteSelection([])
    }

    const isAllRulesSelected = () => {
        if (!rules || !rules.length) {
            return false;
        }
        return rules.every(rule => rulesDeleteSelection.includes(rule._id?.$oid));
    }

    const isRuleSelected = (rule) => {
        return rulesDeleteSelection.includes(rule._id?.$oid);
    }

    const toggleAllRulesSelected = () => {
        setRulesDeleteSelection(isAllRulesSelected() ?
            rulesDeleteSelection.filter(id => !rules.some(rule => rule._id?.$oid === id))
            :
            [...rulesDeleteSelection, ...rules.map((rule) => rule._id?.$oid)]
        );
    }

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (rows) => {
        setPage(1)
        setPageSize(rows)
    }

    const normalizeRuleForEdit = (rule) => {
        const legacyCondition = rule.type ? [{
            type: rule.type,
            parameters: rule.parameters || {},
        }] : [];

        return {
            ...rule,
            scope: rule.scope || DEFAULT_SCOPE,
            phase: rule.phase || DEFAULT_PHASE,
            effect: rule.effect || DEFAULT_EFFECT,
            condition_logic: rule.condition_logic || DEFAULT_CONDITION_LOGIC,
            conditions: Array.isArray(rule.conditions) && rule.conditions.length > 0 ? rule.conditions : legacyCondition,
            message: rule.message || rule.errorMessage || "",
        };
    };

    const prepareRuleForSubmit = (rule) => ({
        ...rule,
        scope: rule.scope || DEFAULT_SCOPE,
        phase: rule.phase || DEFAULT_PHASE,
        effect: rule.active ? (rule.effect || DEFAULT_EFFECT) : rule.effect,
        condition_logic: rule.condition_logic || DEFAULT_CONDITION_LOGIC,
        conditions: (rule.conditions || []).map(condition => {
            const parameters = condition.parameters || {};
            if (condition.type === "category_spend_limit") {
                return {
                    type: condition.type,
                    parameters: normalizeCategorySpendLimitParameters(parameters),
                };
            }
            return {
                type: condition.type,
                parameters,
            };
        }),
        message: rule.message || "",
    });

    const imputationLookup = useMemo(
        () => buildImputationLookup(clientImputations),
        [clientImputations],
    );

    const validateRule = (rule) => {
        const errorKeys = collectExpenseRuleValidationErrorKeys(rule, imputationLookup.liveIds);
        return formatExpenseRuleValidationError(errorKeys, (key) => data.t.read(key));
    };

    const categoryConditionSummary = (condition) => {
        const targets = categoryConditionTargets(condition.parameters);
        const orphanLabel = data.t.read("expense_rules.imputation_target_orphan");
        if (targets.length === 0) {
            return data.t.read("n_a");
        }
        if (targets.length === 1) {
            return categoryTargetSummaryLabel(targets[0], {
                ...imputationLookup,
                orphanLabel,
            });
        }

        const labels = targets.map(target => categoryTargetSummaryLabel(target, {
            ...imputationLookup,
            orphanLabel,
        }));
        const parentCategories = [...new Set(targets.map(target => target.parent_category).filter(Boolean))];
        if (parentCategories.length === 1 && labels.every(label => !label.includes(`(${orphanLabel})`))) {
            return `${parentCategories[0]} (${targets.length} ${data.t.read("expense_rules.subcategories_count")})`;
        }

        return `${targets.length} ${data.t.read("expense_rules.categories_count")}`;
    };

    const ruleConditionsSummary = (rule) => {
        const summaries = (rule.conditions || []).map(condition => {
            if (condition.type === "category_spend_limit") {
                return categoryConditionSummary(condition);
            }
            // Nested key first (the current convention for newer condition types,
            // e.g. expense_rules.employee_rank), then the legacy top-level key
            // (category_spend_limit-era types), then the raw type as a last resort.
            // Translator.read returns undefined for a missing key, not the key
            // itself, so this must not rely on comparing against condition.type.
            return data.t.read(`expense_rules.${condition.type}`) || data.t.read(condition.type) || condition.type;
        }).filter(Boolean);
        return summaries.length > 0 ? summaries.join(", ") : data.t.read("n_a");
    };
    //pagination logic
    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = indexOfFirstItem + rules.length - 1;
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const emptyRowsCount = Math.max(0, pageSize - rules.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    return (
        <div className="card pl-4 pr-4 pb-3 rounded-lg ">
            <div>
                <div className="d-flex pt-4 pl-4 pr-4 pb-4 align-items-center">
                    <h2>{data.t.read("rules")}</h2>
                </div>
                {
                    !loading ?
                        <div>
                            <div>
                                <div className='pagination-row '>
                                    <FilterInputField
                                        filterValue={filterValue} setFilterValue={setFilterValue} placeholder={""}>
                                    </FilterInputField>
                                    <PaginationControls
                                        page={page}
                                        totalPages={totalPages}
                                        indexOfFirstItem={indexOfFirstItem}
                                        indexOfLastItem={indexOfLastItem}
                                        handlePageChange={handlePageChange}
                                        totalItems={totalRowCount}
                                        data={data}
                                        handleRowsPerPageChange={handleRowsPerPageChange}
                                        rowsPerPage={pageSize}
                                    ></PaginationControls>
                                </div>
                                <div className="d-flex justify-content-end align-items-center py-2">
                                    <div className="d-flex">

                                        <button
                                            disabled={rulesDeleteSelection.length === 0}
                                            onClick={() => setShowDeleteRuleModal(true)}
                                            type="button"
                                            className="btn btn-secondary btn-sm ml-2">
                                            {data.t.read("delete") + " (" + rulesDeleteSelection.length + ")"}
                                        </button>
                                        <button
                                            onClick={() => openNewRuleModal()}
                                            type="button"
                                            className='btn btn-primary btn-sm ml-2 justify-content-end'>
                                            {data.t.read("add")}
                                        </button>
                                    </div>
                                </div>
                                <div className="mb-2 card rounded-lg admin-table">
                                    <table className='table table-hover font-small'>
                                        <thead>
                                        <tr>
                                            <th key={`checkbox`}>
                                                <Checkbox
                                                    disabled={!rules || !rules.length}
                                                    isChecked={isAllRulesSelected()}
                                                    onChange={toggleAllRulesSelected}/>
                                            </th>
                                            <th className="" key={"name"}>
                                                {data.t.read("name")}
                                            </th>
                                            <th className="" key={"description"}>
                                                {data.t.read("description")}
                                            </th>
                                            <th className="" key={"effect"}>
                                                {data.t.read("expense_rules.effect")}
                                            </th>
                                            <th className="" key={"conditions"}>
                                                {data.t.read("expense_rules.conditions")}
                                            </th>
                                            <th className="" key={"active"}>
                                                {data.t.read("active")}
                                            </th>
                                            <th className="col-1" key={"actions"}>
                                                Actions
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {
                                            rules.map((rule, key = rule._id) => {
                                                return <tr key={rule._id?.$oid}>
                                                    <td key={`checkbox`}>
                                                        <Checkbox
                                                            isChecked={isRuleSelected(rule)}
                                                            onChange={() => isRuleSelected(rule) ? setRulesDeleteSelection(rulesDeleteSelection.filter(x => x !== rule._id?.$oid)) : setRulesDeleteSelection([...rulesDeleteSelection, rule._id?.$oid])}/>
                                                    </td>
                                                    <td className="" key={"name"} onClick={()=>openUpdateRuleModal(rule)}>
                                                        {rule.name}
                                                    </td>
                                                    <td className="" key={"description"} onClick={()=>openUpdateRuleModal(rule)}>
                                                        {rule.description}
                                                    </td>
                                                    <td className="" key={"effect"} onClick={()=>openUpdateRuleModal(rule)}>
                                                        {rule.effect ? data.t.read(`expense_rules.effect_${rule.effect}`) : "N/A"}
                                                    </td>
                                                    <td className="" key={"conditions"} onClick={()=>openUpdateRuleModal(rule)}>
                                                        <HoverInfoCard info={ruleConditionsSummary(rule)} />
                                                    </td>
                                                    <td className="" key={"active"}>
                                                        <Switch
                                                            className="switch mt-0"
                                                            checked={rule.active}
                                                            width={70}
                                                            onColor="#F39800"
                                                            onHandleColor="#2693e6"
                                                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                            onChange={(active) => {
                                                                const nextRule = normalizeRuleForEdit({...rule, active});
                                                                if (active) {
                                                                    const validationError = validateRule(nextRule);
                                                                    if (validationError) {
                                                                        toast.error(validationError);
                                                                        return;
                                                                    }
                                                                }
                                                                updateRule({...rule, active});
                                                            }}
                                                        />
                                                    </td>
                                                    <td key={"actions"}>
                                                        {<FontAwesomeIcon
                                                            className="mr-2"
                                                            icon={faPencilAlt}
                                                            type="button"
                                                            onClick={() => openUpdateRuleModal(rule)}
                                                        />}
                                                        {<FontAwesomeIcon
                                                            onClick={() => {
                                                                setShowDeleteRuleModal(true);
                                                                setRulesDeleteSelection([rule._id?.$oid]);
                                                            }}
                                                            icon={faWindowClose}
                                                            type="button"
                                                            style={{color: 'red'}}/>}
                                                    </td>
                                                </tr>
                                            })
                                        }
                                        {
                                            emptyRows.map((_, idx) => (
                                                <tr key={`empty-${idx}`}>
                                                    <td colSpan="8">&nbsp;</td>
                                                </tr>
                                            ))
                                        }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {showRuleModal && (
                                <ExpenseRuleEditModal
                                    selectedRule={updatedRule}
                                    setSelectedRule={setUpdatedRule}
                                    data={data}
                                    clientImputations={clientImputations}
                                    onSubmit={handleRuleSubmit}
                                    onCancel={cancelRuleModal}
                                    validationError={ruleValidationError}
                                />
                            )}
                            {showDeleteRuleModal && (
                                <Modal
                                    title="Delete"
                                    okAction={deleteRules}
                                    cancelAction={cancelDeleteRuleModal}
                                    okText={data.t.read("delete")}
                                    cancelText={data.t.read("cancel")}>
                                    Delete

                                </Modal>
                            )}

                        </div>
                        :
                        <div className="d-flex loading-container w-100">
                            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner}/>
                        </div>
                }
            </div>
        </div>
    )
}

export default inject("data")(observer(ExpensesRules));
